import {
  hasKey,
  isAbsent,
  isArray,
  isPresent,
  isString,
  type MapRec,
} from '@yp/common/identity.ts';
import type { SimpleStruct, StrictPropertyList } from '@yp/common/schema/descriptor.ts';

export type ActionHandler<T extends string> = (action: T) => boolean | Promise<boolean>;

// This would be font-normal, but we're seeing the display being too faint on older iOS versions.
export const ModalFont = 'font-sans font-normal';

export type TypedSegment<T extends string | undefined = undefined> = {
  type: 'text' | 'id' | 'icon';
  value: string;
  clazz?: string | undefined;
  link?: string | undefined;
  action?: T;
};
export type PromptSegment<T extends string | undefined = undefined> = string | TypedSegment<T>;
export type PromptDict = Partial<Record<string, string>>;

export function segmentValue(segment: PromptSegment, dict: PromptDict | undefined): string {
  if (isString(segment)) {
    return segment;
  }
  return (segment.type === 'id' ? dict?.[segment.value] : undefined) ?? segment.value;
}

export type PromptLine<T extends string | undefined = undefined> = ReadonlyArray<PromptSegment<T>>;
export type PromptLines<T extends string | undefined = undefined> =
  | PromptLine<T>
  | { lines: readonly PromptLine<T>[]; clazz?: string | undefined };

export function isPromptLine(lines: PromptLines | undefined): lines is PromptLine {
  return isPresent(lines) && !hasKey(lines, 'lines', isArray);
}

export function asLines(lines: PromptLines | undefined): readonly PromptLine[] {
  if (isAbsent(lines)) {
    return [];
  }
  if (isPromptLine(lines)) {
    return [lines];
  }
  return lines.lines;
}

export function classy(text: string, ...classes: string[]): PromptSegment {
  return { type: 'text', value: text, clazz: classes.join(' ') };
}
export const bold = (t: string, ...cl: string[]) => classy(t, 'font-bold', ...cl);
export const italic = (t: string, ...cl: string[]) => classy(t, 'italic', ...cl);
export const period = () => classy('.', '-ml-0.5');

export function textRef(id: string, clazz?: string): PromptSegment {
  return { type: 'id', value: id, clazz };
}
export function icon(icon: string, padded?: boolean): PromptSegment {
  return { type: 'icon', value: icon, clazz: (padded ?? true) ? 'mx-1' : undefined };
}

export type DialogAction<T extends string> = T | 'cancel';
export const CancelAction: DialogAction<never> = 'cancel';

export type DialogInputType = 'input';
export type DialogInput = { label: string; type: DialogInputType };

export type DialogForm<T extends SimpleStruct> = {
  action?: string;
  actionUrl?: string;
  desc: StrictPropertyList<T>;
  value: Partial<T>;
  prompts?: Partial<MapRec<keyof T, PromptLine>>;
  placeholders?: Partial<MapRec<keyof T, string>>;
  errors?: Partial<MapRec<keyof T, string>>;
  hidden?: Array<keyof T>;
};
